/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { Link } from 'react-router';
import { useStyletron } from 'baseui';

import { Button } from '@letsdothis/lds/lib/Button';
import {
  LinkButton,
  LinkButtonType,
} from '@letsdothis/lds/lib/candidate/LinkButton';

import { useAuthService } from '@/contexts/authService';
import { logError } from '@/utils/logError';

import Field from '../../../Form/Fields/Field';
import * as styles from '../styles.module.css';
import FieldValidators from '../FieldValidators';
import { LoginParams, LoginFormParams } from './Types';
import { validateLoginParams, validateLoginFormParams } from './Validator';
import handlePostLoginActions from '../../../../views/Cognito/LoginCallback/handlePostLoginActions';

const login = async (authService: any, params: LoginParams): Promise<void> => {
  validateLoginParams(params);
  const {
    email,
    previousEmailError,
    password,
    previousPasswordError,
    setState,
    returnUrl,
  } = params;

  const emailError = FieldValidators.validateEmail(email);
  if (emailError !== previousEmailError) {
    setState({ emailError });
  }
  const passwordError = FieldValidators.validatePassword(password);
  if (passwordError !== previousPasswordError) {
    setState({ passwordError });
  }

  if (!emailError && !passwordError) {
    await authService.loginEmailPassword({
      returnUrl,
      email,
      password,
    });
    window.location.href = returnUrl;
  } else {
    throw new Error('Validation error on login');
  }
};

const LoginForm = (params: LoginFormParams): JSX.Element => {
  const [css, theme] = useStyletron();
  validateLoginFormParams(params);
  const authService = useAuthService();
  const {
    returnUrl,
    signInOrUpButtonLoading,
    email,
    emailError,
    password,
    passwordError,
    setState,
    signInOrUpError,
  } = params;
  const setEmail = (newEmail?: string) => setState({ email: newEmail });
  const setPassword = (newPassword?: string) =>
    setState({ password: newPassword });
  const setForgotPassword = (): void => setState({ forgotPassword: true });

  return (
    <div className={styles.emailPasswordContainer}>
      {Field({
        title: 'Email',
        value: email,
        setValue: setEmail,
        error: emailError,
      })}
      {Field({
        title: 'Password',
        hidden: true,
        value: password,
        setValue: setPassword,
        error: passwordError,
        overlay: {
          title: 'Forgot Password?',
          action: setForgotPassword,
        },
      })}
      <Button
        isLoading={signInOrUpButtonLoading}
        onClick={() => {
          setState({ signInOrUpButtonLoading: true });
          setState({ signInOrUpError: undefined });
          login(authService, {
            email,
            previousEmailError: emailError,
            password,
            previousPasswordError: passwordError,
            setState,
            returnUrl,
          })
            .then(() => {
              setState({ signInOrUpButtonLoading: false });
              handlePostLoginActions({
                ...params,
                authService,
                noRedirect: true,
              });
            })
            .catch((error) => {
              setState({ signInOrUpButtonLoading: false });
              setState({
                signInOrUpError:
                  'Invalid password or email.  Please try again.',
              });
              logError(error, {
                errorMessageFallback: 'Error while logging in.',
              });
            });
        }}
      >
        Login with email
      </Button>
      {signInOrUpError && (
        <div className={css({ color: theme.colors.negative400 })}>
          {signInOrUpError}
        </div>
      )}
      <div className={styles.signUpSignInSwitchText}>
        Don’t have an account yet?
        <LinkButton
          onClick={() => setState({ isSigningUp: true })}
          type={LinkButtonType.Label}
        >
          <div className={styles.linkText}>Sign up</div>
        </LinkButton>
      </div>
      <div className={styles.terms}>
        By creating an account, you accept our <Link to="/terms">terms</Link>
      </div>
    </div>
  );
};

export default LoginForm;
